/**
 * Utilidades para compresión y optimización de imágenes en el cliente (Browser).
 * Permite comprimir imágenes pesadas (16MB+, fotos de alta resolución, capturas 4K)
 * para cumplir con los estándares de WhatsApp Cloud API (máximo 5 MB para imágenes).
 */

const DEFAULT_OPTIONS = {
  maxBytes: 4 * 1024 * 1024, // 4 MB (holgadamente por debajo de los 5 MB de WhatsApp)
  maxWidth: 2048,
  maxHeight: 2048,
  initialQuality: 0.82
};

/**
 * Comprime un archivo de imagen (File o Blob) si supera el tamaño o dimensiones objetivo.
 *
 * @param {File|Blob} file - Archivo de imagen a comprimir.
 * @param {Object} [options] - Opciones de compresión.
 * @returns {Promise<File>} Archivo comprimido o el original si no requiere compresión.
 */
export const compressImageFile = async (file, options = {}) => {
  if (!file || !file.type || !file.type.startsWith('image/')) {
    return file;
  }

  // No comprimir por canvas formatos especiales vectoriales o animados
  if (file.type === 'image/gif' || file.type === 'image/svg+xml') {
    return file;
  }

  const config = { ...DEFAULT_OPTIONS, ...options };

  // Si ya es un JPEG ligero (<= 1.5 MB), no es necesario recompilarlo
  if (file.type === 'image/jpeg' && file.size <= 1.5 * 1024 * 1024) {
    return file;
  }

  return new Promise(resolve => {
    let objectUrl = null;
    try {
      objectUrl = URL.createObjectURL(file);
    } catch {
      resolve(file);
      return;
    }

    const img = new Image();

    img.onload = () => {
      try {
        URL.revokeObjectURL(objectUrl);
      } catch {
        // Ignorar error al revocar
      }

      let { width, height } = img;

      // Calcular nuevas dimensiones manteniendo aspect ratio
      if (width > config.maxWidth || height > config.maxHeight) {
        if (width > height) {
          height = Math.round((height * config.maxWidth) / width);
          width = config.maxWidth;
        } else {
          width = Math.round((width * config.maxHeight) / height);
          height = config.maxHeight;
        }
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        resolve(file);
        return;
      }

      // Fondo blanco por si la imagen original tenía canal alfa transparente
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);

      const tryExport = quality => {
        canvas.toBlob(
          blob => {
            if (!blob) {
              resolve(file);
              return;
            }

            // Si el blob resultante sigue superando el límite y podemos reducir más la calidad
            if (blob.size > config.maxBytes && quality > 0.5) {
              tryExport(quality - 0.15);
              return;
            }

            const originalName = file.name || 'imagen.jpg';
            const cleanName = originalName.replace(/\.[^/.]+$/, '') + '.jpg';
            const compressedFile = new File([blob], cleanName, {
              type: 'image/jpeg',
              lastModified: Date.now()
            });

            resolve(compressedFile);
          },
          'image/jpeg',
          quality
        );
      };

      tryExport(config.initialQuality);
    };

    img.onerror = () => {
      try {
        URL.revokeObjectURL(objectUrl);
      } catch {
        // Ignorar error al revocar
      }
      resolve(file);
    };

    img.src = objectUrl;
  });
};

/**
 * Convierte y comprime el contenido de un Canvas HTML a un archivo optimizado para WhatsApp.
 *
 * @param {HTMLCanvasElement} canvas - Elemento canvas a exportar.
 * @param {Object} [options] - Opciones de exportación.
 * @returns {Promise<{ file: File, blob: Blob, url: string, name: string, size: number } | null>}
 */
export const compressCanvas = (canvas, options = {}) => {
  if (!canvas) return Promise.resolve(null);

  const config = { ...DEFAULT_OPTIONS, ...options };
  const fileName = options.fileName || 'imagen_editada.jpg';

  return new Promise(resolve => {
    let { width, height } = canvas;
    let targetCanvas = canvas;

    // Escalar si el canvas excede las dimensiones máximas
    if (width > config.maxWidth || height > config.maxHeight) {
      if (width > height) {
        height = Math.round((height * config.maxWidth) / width);
        width = config.maxWidth;
      } else {
        width = Math.round((width * config.maxHeight) / height);
        height = config.maxHeight;
      }

      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = width;
      tempCanvas.height = height;
      const ctx = tempCanvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(canvas, 0, 0, width, height);
        targetCanvas = tempCanvas;
      }
    }

    const tryExport = quality => {
      targetCanvas.toBlob(
        blob => {
          if (!blob) {
            resolve(null);
            return;
          }

          if (blob.size > config.maxBytes && quality > 0.5) {
            tryExport(quality - 0.15);
            return;
          }

          const cleanName = fileName.replace(/\.[^/.]+$/, '') + '.jpg';
          const file = new File([blob], cleanName, {
            type: 'image/jpeg',
            lastModified: Date.now()
          });
          const url = URL.createObjectURL(blob);

          resolve({
            file,
            blob,
            url,
            name: cleanName,
            size: blob.size,
            type: 'image'
          });
        },
        'image/jpeg',
        quality
      );
    };

    tryExport(config.initialQuality);
  });
};
